/** @graphql cc **/
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import React, { type FC, useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, xcss, Text } from '@atlaskit/primitives';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import { IconTile } from '@atlaskit/icon';

import { fg } from '@confluence/feature-gating';

import type { GroupOrTeamPrincipal } from '../../../../commonTypes';

import type {
	GroupDetailsQuery as GroupDetailsQuery$data,
	GroupDetailsQueryVariables as GroupDetailsQuery$variables,
} from './__types__/GroupDetailsQuery';

const GroupDetailsQuery = gql`
	query GroupDetailsQuery($groupIdList: [String]) {
		groupCounts(groupIds: $groupIdList) {
			groupCounts {
				key
				value
			}
		}
	}
`;

const i18n = defineMessages({
	groupText: {
		id: 'share-and-restrict-dialog.principal-details.group-text',
		defaultMessage: 'Group',
		description: 'Label indicating that an entity in a permissions table is a group',
	},
	teamText: {
		id: 'share-and-restrict-dialog.principal-details.team-text',
		defaultMessage: 'Team',
		description: 'Label indicating that an entity in a permissions table is a team',
	},
	groupUsersCount: {
		id: 'share-and-restrict-dialog.principal-details.group-count.one-person',
		defaultMessage: '{count, plural, one {1 person} other {{count} people}}',
		description: 'user count subtext for group',
	},
	teamMembersCount: {
		id: 'share-and-restrict-dialog.principal-details.team-count.one-person',
		defaultMessage: '{count, plural, one {1 member} other {{count} members}}',
		description: 'user count subtext for team',
	},
});

const principalDetailsStyle = xcss({
	overflowWrap: 'anywhere',
});

const iconContainer = xcss({
	borderRadius: 'border.radius.circle',
	width: '36px',
	height: '36px',
	backgroundColor: 'color.background.neutral',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexShrink: 0,
	marginTop: 'space.0',
});

export const GroupDetails: FC<GroupOrTeamPrincipal> = ({ name, id, usageType, type }) => {
	const { formatMessage } = useIntl();
	const [count, setCount] = useState<number | null | undefined>(null);
	const { loading, data } = useQuery<GroupDetailsQuery$data, GroupDetailsQuery$variables>(
		GroupDetailsQuery,
		{
			variables: { groupIdList: [id] },
		},
	);

	useEffect(() => {
		if (!loading && data) {
			setCount(data?.groupCounts?.groupCounts?.[0]?.value);
		}
	}, [loading, data]);

	const isATeamType = usageType === 'TEAM_COLLABORATION' || type === 'team';

	return (
		<Inline space="space.150" alignBlock="center">
			<Box xcss={iconContainer}>
				<IconTile label="" size="32" appearance="gray" shape="circle" icon={PeopleGroupIcon} />
			</Box>
			<Stack xcss={principalDetailsStyle}>
				<Inline>{name}</Inline>
				{isATeamType && fg('confluence_frontend_perms_exp_teams_as_principals') ? (
					<Text size="small" color="color.text.subtlest">
						{`${formatMessage(i18n.teamText)} • ${formatMessage(i18n.teamMembersCount, { count })}`}
					</Text>
				) : (
					<Text size="small" color="color.text.subtlest">
						{`${formatMessage(i18n.groupText)}${
							fg('confluence_frontend_perms_exp_teams_as_principals')
								? ` • ${formatMessage(i18n.groupUsersCount, { count })}`
								: ''
						}`}
					</Text>
				)}
			</Stack>
		</Inline>
	);
};
